import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft,
  Bell,
  CheckCircle2,
  Circle,
  CircleAlert,
  Code2,
  Clock3,
  Eye,
  EyeOff,
  FileText,
  KeyRound,
  LayoutGrid,
  LayoutList,
  LogOut,
  Mail,
  PlusSquare,
  ShieldCheck,
  Ticket,
  Upload,
  UserRound,
  Users,
} from "lucide-react";

import RichTextEditor, { getPlainTextFromHtml } from "../components/RichTextEditor";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import Header from "./Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "support_access_token";
const MAX_TICKET_ATTACHMENTS = 10;

const initialLogin = {
  email: "",
  password: "",
};

const initialAdminClient = {
  id: "",
  full_name: "",
  email: "",
  password: "",
  managed_developer_id: "",
  tma_enabled: false,
  tma_visible_to_client: false,
  tma_hours_total: "",
};

const initialAdminDeveloper = {
  id: "",
  full_name: "",
  email: "",
  password: "",
  can_access_platform: true,
};

const initialForgotPassword = {
  email: "",
};

const initialResetPassword = {
  token: "",
  new_password: "",
  confirm_password: "",
};

const initialTicket = {
  title: "",
  category: "modification-contenu",
  site_url: "",
  description: "",
};

const STATUS_OPTIONS = [
  { value: "ticket_ouvert", label: "Ticket ouvert" },
  { value: "en_cours_de_traitement", label: "En cours de traitement" },
  { value: "traite", label: "Traite" },
  { value: "demande_info_supplementaires", label: "Demande d'informations supplementaires" },
];

const getPasswordChecks = (password, confirmPassword = "", includeMatchCheck = false) => {
  const checks = [
    { label: "Au moins 10 caracteres", valid: password.length >= 10 },
    { label: "Une majuscule", valid: /[A-Z]/.test(password) },
    { label: "Une minuscule", valid: /[a-z]/.test(password) },
    { label: "Un chiffre", valid: /\d/.test(password) },
  ];

  if (includeMatchCheck) {
    checks.push({
      label: "Les deux mots de passe sont identiques",
      valid: Boolean(password) && Boolean(confirmPassword) && password === confirmPassword,
    });
  }

  return checks;
};

const getPasswordStrength = (password, confirmPassword = "", includeMatchCheck = false) => {
  const checks = getPasswordChecks(password, confirmPassword, includeMatchCheck);
  const passed = checks.filter((item) => item.valid).length;

  if (!password) {
    return { label: "A completer", tone: "neutral", checks };
  }
  if (passed === checks.length) {
    return { label: "Mot de passe acceptable", tone: "success", checks };
  }
  if (passed >= 2) {
    return { label: "Presque bon", tone: "warning", checks };
  }
  return { label: "Trop faible", tone: "danger", checks };
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (item?.loc?.length) {
          const fieldName = item.loc[item.loc.length - 1];
          return `${fieldName} : ${item.msg}`;
        }
        return item?.msg || "Erreur de validation.";
      })
      .join(" | ");
  }

  if (typeof detail === "string") {
    return detail;
  }

  if (typeof error?.message === "string" && !error.response) {
    return error.message;
  }

  return fallbackMessage;
};

const hasValidationErrorForField = (error, fieldName) => {
  const detail = error?.response?.data?.detail;

  if (!Array.isArray(detail)) {
    return false;
  }

  return detail.some((item) => {
    if (!Array.isArray(item?.loc)) {
      return false;
    }
    return item.loc[item.loc.length - 1] === fieldName;
  });
};

const getPreviewText = (html, maxLength = 150) => {
  const plain = getPlainTextFromHtml(html || "");
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength).trim()}...`;
};

function PasswordRequirements({ password, confirmPassword = "", showMatchCheck = false }) {
  const strength = getPasswordStrength(password, confirmPassword, showMatchCheck);

  return (
    <div className="support-password-box">
      <div className={`support-password-status is-${strength.tone}`}>
        <KeyRound className="w-4 h-4" />
        {strength.label}
      </div>
      <div className="support-password-rules">
        {strength.checks.map((check) => (
          <div key={check.label} className={`support-password-rule ${check.valid ? "is-valid" : ""}`}>
            {check.valid ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
            <span>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PasswordField({
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  isVisible,
  onToggleVisibility,
}) {
  return (
    <div className="support-password-input-wrap">
      <input
        className="form-input support-password-input"
        type={isVisible ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
      />
      <button
        type="button"
        className="support-password-toggle"
        onClick={onToggleVisibility}
        aria-label={isVisible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        aria-pressed={isVisible}
      >
        {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        <span>{isVisible ? "Masquer" : "Afficher"}</span>
      </button>
    </div>
  );
}

export default function SupportPortal() {
  const { toast } = useToast();
  const [mode, setMode] = useState("login");
  const [activeView, setActiveView] = useState("tickets");
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [adminClients, setAdminClients] = useState([]);
  const [adminDevelopers, setAdminDevelopers] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loginForm, setLoginForm] = useState(initialLogin);
  const [adminClientForm, setAdminClientForm] = useState(initialAdminClient);
  const [adminDeveloperForm, setAdminDeveloperForm] = useState(initialAdminDeveloper);
  const [forgotPasswordForm, setForgotPasswordForm] = useState(initialForgotPassword);
  const [resetPasswordForm, setResetPasswordForm] = useState(initialResetPassword);
  const [ticketForm, setTicketForm] = useState(initialTicket);
  const [ticketFiles, setTicketFiles] = useState([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [adminStatus, setAdminStatus] = useState("ticket_ouvert");
  const [adminComment, setAdminComment] = useState("");
  const [developmentTimeHours, setDevelopmentTimeHours] = useState("");
  const [ticketDisplayMode, setTicketDisplayMode] = useState("list");
  const [isDeleteClientDialogOpen, setIsDeleteClientDialogOpen] = useState(false);
  const [isClientEditorOpen, setIsClientEditorOpen] = useState(false);
  const [isDeveloperEditorOpen, setIsDeveloperEditorOpen] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showAdminClientPassword, setShowAdminClientPassword] = useState(false);
  const [showAdminDeveloperPassword, setShowAdminDeveloperPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);

  const isEditingClient = Boolean(adminClientForm.id);
  const isEditingDeveloper = Boolean(adminDeveloperForm.id);
  const activeDevelopers = useMemo(
    () => adminDevelopers.filter((developer) => developer.is_active && developer.can_access_platform),
    [adminDevelopers]
  );
  const resetPasswordChecks = useMemo(
    () => getPasswordChecks(resetPasswordForm.new_password, resetPasswordForm.confirm_password, true),
    [resetPasswordForm.new_password, resetPasswordForm.confirm_password]
  );
  const isResetPasswordFormValid = useMemo(
    () => Boolean(resetPasswordForm.token) && resetPasswordChecks.every((check) => check.valid),
    [resetPasswordChecks, resetPasswordForm.token]
  );

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const filteredTickets = useMemo(() => {
    if (!selectedClientId) return tickets;
    return tickets.filter((ticket) => ticket.client_id === selectedClientId);
  }, [tickets, selectedClientId]);

  const kanbanColumns = useMemo(
    () =>
      STATUS_OPTIONS.map((statusOption) => ({
        ...statusOption,
        tickets: filteredTickets.filter((ticket) => ticket.status === statusOption.value),
      })),
    [filteredTickets]
  );

  const formatDate = (value) =>
    new Date(value).toLocaleString("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const routeMode = params.get("mode");
    const resetToken = params.get("token");

    if (routeMode === "reset") {
      setMode("reset");
      setResetPasswordForm((current) => ({
        ...current,
        token: resetToken || "",
      }));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
    setTickets([]);
    setAdminClients([]);
    setAdminDevelopers([]);
    setSelectedTicketId("");
    setSelectedTicket(null);
    setSelectedClientId("");
    setNotifications([]);
    setUnreadCount(0);
    setActiveView("tickets");
  };

  const fetchDashboard = async (authToken = token) => {
    if (!authToken) return;

    setLoadingData(true);
    try {
      const headers = { headers: { Authorization: `Bearer ${authToken}` } };
      const meRes = await axios.get(`${API}/auth/me`, headers);
      const nextUser = meRes.data;
      const [ticketsRes, notifRes] = await Promise.all([
        axios.get(`${API}/tickets`, headers),
        axios.get(`${API}/tickets/notifications/list`, headers),
      ]);
      const nextTickets = ticketsRes.data;

      setUser(nextUser);
      setTickets(nextTickets);
      setNotifications(notifRes.data.items);
      setUnreadCount(notifRes.data.unread_count);

      if (nextUser.role === "admin") {
        try {
          const clientsRes = await axios.get(`${API}/auth/admin/clients`, headers);
          setAdminClients(clientsRes.data);
        } catch (error) {
          setAdminClients([]);
        }

        try {
          const developersRes = await axios.get(`${API}/auth/admin/developers`, headers);
          setAdminDevelopers(developersRes.data);
        } catch (error) {
          setAdminDevelopers([]);
        }
      } else {
        setAdminClients([]);
        setAdminDevelopers([]);
      }

      if (nextUser.role !== "admin") {
        setSelectedClientId("");
      }

      if (nextTickets.length > 0) {
        const availableIds = nextTickets.map((ticket) => ticket.id);
        const targetId = availableIds.includes(selectedTicketId) ? selectedTicketId : nextTickets[0].id;
        const detailRes = await axios.get(`${API}/tickets/${targetId}`, headers);
        setSelectedTicket(detailRes.data);
        setSelectedTicketId(detailRes.data.id);
        setAdminStatus(detailRes.data.status);
        setDevelopmentTimeHours(detailRes.data.development_time_hours ?? "");
      } else {
        setSelectedTicket(null);
        setSelectedTicketId("");
        setDevelopmentTimeHours("");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!token) return undefined;
    fetchDashboard(token);
    const intervalId = window.setInterval(() => fetchDashboard(token), 30000);
    return () => window.clearInterval(intervalId);
  }, [token]);

  const handleAuthSuccess = async (response) => {
    const nextToken = response.data.access_token;
    localStorage.setItem(TOKEN_KEY, nextToken);
    setToken(nextToken);
    setUser(response.data.user);
    setActiveView("tickets");
    setSelectedClientId("");
    setSelectedTicketId("");
    setSelectedTicket(null);
    setTickets([]);
    setAdminClients([]);
    setAdminDevelopers([]);
    toast({
      title: "Connexion reussie",
      description: "Votre espace support est disponible.",
    });
    await fetchDashboard(nextToken);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      await handleAuthSuccess(response);
      setLoginForm(initialLogin);
    } catch (error) {
      toast({
        title: "Connexion impossible",
        description: getApiErrorMessage(error, "Identifiants invalides."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, forgotPasswordForm);
      toast({
        title: "Reinitialisation envoyee",
        description: response.data.message,
      });
      setForgotPasswordForm(initialForgotPassword);
      setMode("login");
    } catch (error) {
      toast({
        title: "Demande impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (resetPasswordForm.new_password !== resetPasswordForm.confirm_password) {
      toast({
        title: "Confirmation incorrecte",
        description: "Les deux mots de passe doivent etre identiques.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: resetPasswordForm.token,
        new_password: resetPasswordForm.new_password,
      });
      toast({
        title: "Mot de passe reinitialise",
        description: response.data.message,
      });
      setResetPasswordForm(initialResetPassword);
      setMode("login");
      window.history.replaceState({}, document.title, "/espace-client");
    } catch (error) {
      const isLegacyBackendContractError =
        error?.response?.status === 422 && hasValidationErrorForField(error, "email");

      toast({
        title: "Reinitialisation impossible",
        description: isLegacyBackendContractError
          ? "Le frontend utilise le nouveau lien de reinitialisation sans email, mais l'API de production attend encore l'ancien format. Il faut redeployer le backend Railway."
          : getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const payload = {
        full_name: adminClientForm.full_name,
        email: adminClientForm.email,
        managed_developer_id: adminClientForm.managed_developer_id || null,
        tma_enabled: adminClientForm.tma_enabled,
        tma_visible_to_client: adminClientForm.tma_enabled && adminClientForm.tma_visible_to_client,
        tma_hours_total: Number(adminClientForm.tma_hours_total || 0),
        ...(adminClientForm.password ? { password: adminClientForm.password } : {}),
      };
      const response = isEditingClient
        ? await axios.patch(`${API}/auth/admin/clients/${adminClientForm.id}`, payload, authHeaders)
        : await axios.post(`${API}/auth/admin/clients`, payload, authHeaders);
      setAdminClientForm(initialAdminClient);
      setIsClientEditorOpen(false);
      setSelectedClientId(response.data.id);
      await fetchDashboard();
      toast({
        title: isEditingClient ? "Client mis a jour" : "Acces client cree",
        description: isEditingClient
          ? "Les informations du client ont ete mises a jour."
          : "Le client peut maintenant se connecter ou utiliser Mot de passe oublie plus tard.",
      });
    } catch (error) {
      toast({
        title: isEditingClient ? "Mise a jour impossible" : "Creation du client impossible",
        description: getApiErrorMessage(error, "Verifiez les informations saisies."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = (client) => {
    setAdminClientForm({
      id: client.id,
      full_name: client.full_name,
      email: client.email,
      password: "",
      managed_developer_id: client.managed_developer_id || "",
      tma_enabled: Boolean(client.tma_enabled),
      tma_visible_to_client: Boolean(client.tma_visible_to_client),
      tma_hours_total: client.tma_hours_total ?? "",
    });
    setIsClientEditorOpen(true);
    setSelectedClientId(client.id);
  };

  const resetClientForm = () => {
    setAdminClientForm(initialAdminClient);
    setIsClientEditorOpen(false);
    setSelectedClientId("");
  };

  const openCreateClientForm = () => {
    setAdminClientForm(initialAdminClient);
    setSelectedClientId("");
    setIsClientEditorOpen(true);
  };

  const handleDeleteClient = async () => {
    if (!adminClientForm.id) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/auth/admin/clients/${adminClientForm.id}`, authHeaders);
      setIsDeleteClientDialogOpen(false);
      resetClientForm();
      setSelectedTicket(null);
      setSelectedTicketId("");
      await fetchDashboard();
      toast({
        title: "Client bloque",
        description: "Son acces est coupe, mais ses tickets et son historique restent conserves.",
      });
    } catch (error) {
      toast({
        title: "Suppression impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openTicket = async (ticketId) => {
    setSelectedTicketId(ticketId);
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, authHeaders);
      setSelectedTicket(response.data);
      setAdminStatus(response.data.status);
      setDevelopmentTimeHours(response.data.development_time_hours ?? "");
      setActiveView("ticket-detail");
    } catch (error) {
      toast({
        title: "Ticket indisponible",
        description: "Impossible de charger ce ticket.",
        variant: "destructive",
      });
    }
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    if (!getPlainTextFromHtml(ticketForm.description)) {
      toast({
        title: "Description requise",
        description: "Ajoute le contenu de ta demande avant l'envoi.",
        variant: "destructive",
      });
      return;
    }
    if (Array.from(ticketFiles).length > MAX_TICKET_ATTACHMENTS) {
      toast({
        title: "Trop de fichiers",
        description: `Vous pouvez ajouter jusqu'a ${MAX_TICKET_ATTACHMENTS} pieces jointes par ticket.`,
        variant: "destructive",
      });
      return;
    }
    const formData = new FormData();
    formData.append("title", ticketForm.title);
    formData.append("category", ticketForm.category);
    formData.append("site_url", ticketForm.site_url);
    formData.append("description", ticketForm.description);
    Array.from(ticketFiles).forEach((file) => formData.append("attachments", file));

    setLoading(true);
    try {
      await axios.post(`${API}/tickets`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setTicketForm(initialTicket);
      setTicketFiles([]);
      setActiveView("tickets");
      await fetchDashboard();
      toast({
        title: "Ticket envoye",
        description: "Votre demande de modification a bien ete transmise.",
      });
    } catch (error) {
      toast({
        title: "Creation impossible",
        description: getApiErrorMessage(error, "Le ticket n'a pas pu etre cree."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeveloper = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const payload = {
        full_name: adminDeveloperForm.full_name,
        email: adminDeveloperForm.email,
        can_access_platform: adminDeveloperForm.can_access_platform,
        ...(adminDeveloperForm.password ? { password: adminDeveloperForm.password } : {}),
      };
      const response = isEditingDeveloper
        ? await axios.patch(`${API}/auth/admin/developers/${adminDeveloperForm.id}`, payload, authHeaders)
        : await axios.post(`${API}/auth/admin/developers`, payload, authHeaders);
      setAdminDeveloperForm(initialAdminDeveloper);
      setIsDeveloperEditorOpen(false);
      await fetchDashboard();
      toast({
        title: isEditingDeveloper ? "Developpeur mis a jour" : "Acces developpeur cree",
        description: response.data.can_access_platform
          ? "Le compte developpeur peut acceder a la plateforme."
          : "Le compte developpeur est cree sans acces plateforme.",
      });
    } catch (error) {
      toast({
        title: isEditingDeveloper ? "Mise a jour impossible" : "Creation du developpeur impossible",
        description: getApiErrorMessage(error, "Verifiez les informations saisies."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditDeveloper = (developer) => {
    setAdminDeveloperForm({
      id: developer.id,
      full_name: developer.full_name,
      email: developer.email,
      password: "",
      can_access_platform: developer.can_access_platform,
    });
    setIsDeveloperEditorOpen(true);
  };

  const resetDeveloperForm = () => {
    setAdminDeveloperForm(initialAdminDeveloper);
    setIsDeveloperEditorOpen(false);
  };

  const openCreateDeveloperForm = () => {
    setAdminDeveloperForm(initialAdminDeveloper);
    setIsDeveloperEditorOpen(true);
  };

  const handleDeleteDeveloper = async () => {
    if (!adminDeveloperForm.id) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/auth/admin/developers/${adminDeveloperForm.id}`, authHeaders);
      resetDeveloperForm();
      await fetchDashboard();
      toast({
        title: "Developpeur bloque",
        description: "Son acces est coupe, mais l'historique reste conserve.",
      });
    } catch (error) {
      toast({
        title: "Blocage impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTicketFilesChange = (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length > MAX_TICKET_ATTACHMENTS) {
      event.target.value = "";
      setTicketFiles([]);
      toast({
        title: "Trop de fichiers",
        description: `Vous pouvez ajouter jusqu'a ${MAX_TICKET_ATTACHMENTS} pieces jointes par ticket.`,
        variant: "destructive",
      });
      return;
    }

    setTicketFiles(event.target.files);
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!selectedTicket || !getPlainTextFromHtml(messageDraft)) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/tickets/${selectedTicket.id}/messages`,
        { content: messageDraft },
        authHeaders
      );
      setSelectedTicket(response.data);
      setMessageDraft("");
      await fetchDashboard();
    } catch (error) {
      toast({
        title: "Message non envoye",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (event) => {
    event.preventDefault();
    if (!selectedTicket) return;

    setLoading(true);
    try {
      const normalizedComment = getPlainTextFromHtml(adminComment) ? adminComment : null;
      const normalizedDevelopmentTime =
        developmentTimeHours === "" || developmentTimeHours === null
          ? null
          : Number(developmentTimeHours);
      const response = await axios.patch(
        `${API}/tickets/${selectedTicket.id}/status`,
        {
          status: adminStatus,
          comment: normalizedComment,
          ...(normalizedDevelopmentTime !== null ? { development_time_hours: normalizedDevelopmentTime } : {}),
        },
        authHeaders
      );
      setSelectedTicket(response.data);
      setAdminComment("");
      setDevelopmentTimeHours(response.data.development_time_hours ?? "");
      await fetchDashboard();
      toast({
        title: "Statut mis a jour",
        description: "Le client a ete notifie dans l'espace support et par email.",
      });
    } catch (error) {
      toast({
        title: "Mise a jour impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTicketAssignmentChange = async (developerId) => {
    if (!selectedTicket) return;

    setLoading(true);
    try {
      const response = await axios.patch(
        `${API}/tickets/${selectedTicket.id}/assignment`,
        { developer_id: developerId || null },
        authHeaders
      );
      setSelectedTicket(response.data);
      await fetchDashboard();
      toast({
        title: developerId ? "Ticket attribue" : "Affectation retiree",
        description: developerId
          ? "Le developpeur a ete notifie par email."
          : "Le ticket n'est plus attribue a un developpeur.",
      });
    } catch (error) {
      toast({
        title: "Affectation impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/tickets/notifications/${notificationId}/read`, {}, authHeaders);
      await fetchDashboard();
    } catch (error) {
      toast({
        title: "Notification",
        description: "Impossible de marquer cette notification comme lue.",
        variant: "destructive",
      });
    }
  };

  const downloadAttachment = async (attachment) => {
    if (!selectedTicket) return;

    try {
      const response = await axios.get(
        `${API}/tickets/${selectedTicket.id}/attachments/${attachment.id}`,
        {
          ...authHeaders,
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Telechargement impossible",
        description: "La piece jointe n'a pas pu etre recuperee.",
        variant: "destructive",
      });
    }
  };

  const clientMenuItems = [
    { id: "tickets", label: "Tickets", icon: LayoutList },
    { id: "new-ticket", label: "Ajouter un ticket", icon: PlusSquare },
    { id: "notifications", label: "Notifications", icon: Bell, badge: unreadCount },
    { id: "profile", label: "Infos client", icon: UserRound },
  ];

  const adminMenuItems = [
    { id: "tickets", label: "Tickets", icon: LayoutList },
    { id: "clients", label: "Clients", icon: Users },
    { id: "developers", label: "Developpeurs", icon: Code2 },
    { id: "notifications", label: "Notifications", icon: Bell, badge: unreadCount },
    { id: "profile", label: "Infos support", icon: UserRound },
  ];

  const developerMenuItems = [
    { id: "tickets", label: "Tickets", icon: LayoutList },
    { id: "notifications", label: "Notifications", icon: Bell, badge: unreadCount },
    { id: "profile", label: "Infos developpeur", icon: Code2 },
  ];

  const menuItems = user?.role === "admin" ? adminMenuItems : user?.role === "developer" ? developerMenuItems : clientMenuItems;

  const renderAuthView = () => (
    <section className="support-hero">
      <div className="container support-hero-grid">
        <div>
          <div className="hero-announcement">
            <ShieldCheck className="w-4 h-4" />
            Support securise
          </div>
          <h1 className="heading-1 mb-4">Espace client et gestion des tickets</h1>
          <p className="body-large">
            Connexion securisee, tickets par site, pieces jointes, suivi des statuts,
            reponses support et centre de notifications interne.
          </p>
          <div className="support-feature-list">
            <div><Ticket className="w-4 h-4" /> Tickets organises</div>
            <div><Upload className="w-4 h-4" /> Pieces jointes</div>
            <div><Bell className="w-4 h-4" /> Alertes internes</div>
          </div>
        </div>

        <Card className="support-auth-card">
          <CardHeader>
            <CardTitle className="heading-2">
              {mode === "login" && "Connexion"}
              {mode === "forgot" && "Mot de passe oublie"}
              {mode === "reset" && "Creer un nouveau mot de passe"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mode === "login" && (
              <form onSubmit={handleLogin} className="support-form-stack">
                <input className="form-input" type="email" placeholder="Email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} required />
                <PasswordField
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="Mot de passe"
                  required
                  isVisible={showLoginPassword}
                  onToggleVisibility={() => setShowLoginPassword((current) => !current)}
                />
                <p className="body-small support-auth-note">
                  Les acces clients sont crees par l'administration.
                </p>
                <button type="button" className="support-text-link" onClick={() => { setForgotPasswordForm({ email: loginForm.email }); setMode("forgot"); }}>
                  Mot de passe oublie ?
                </button>
                <Button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? "Connexion..." : "Se connecter"}
                </Button>
              </form>
            )}

            {mode === "forgot" && (
              <form onSubmit={handleForgotPassword} className="support-form-stack">
                <input className="form-input" type="email" placeholder="Email du compte" value={forgotPasswordForm.email} onChange={(e) => setForgotPasswordForm({ email: e.target.value })} required />
                <p className="body-small">Nous enverrons un lien de reinitialisation si le compte existe.</p>
                <Button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? "Envoi..." : "Recevoir un lien"}
                </Button>
                <button type="button" className="support-text-link" onClick={() => setMode("login")}>Retour a la connexion</button>
              </form>
            )}

            {mode === "reset" && (
              <form onSubmit={handleResetPassword} className="support-form-stack">
                {!resetPasswordForm.token && (
                  <p className="body-small support-auth-note">
                    Ouvre cette page depuis le lien de reinitialisation recu par email.
                  </p>
                )}
                <PasswordField
                  value={resetPasswordForm.new_password}
                  onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, new_password: e.target.value })}
                  placeholder="Nouveau mot de passe"
                  required
                  isVisible={showResetPassword}
                  onToggleVisibility={() => setShowResetPassword((current) => !current)}
                />
                <PasswordField
                  value={resetPasswordForm.confirm_password}
                  onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirm_password: e.target.value })}
                  placeholder="Confirmer le nouveau mot de passe"
                  required
                  isVisible={showResetPasswordConfirm}
                  onToggleVisibility={() => setShowResetPasswordConfirm((current) => !current)}
                />
                <PasswordRequirements
                  password={resetPasswordForm.new_password}
                  confirmPassword={resetPasswordForm.confirm_password}
                  showMatchCheck
                />
                <Button type="submit" className="btn-primary w-full" disabled={loading || !isResetPasswordFormValid}>
                  {loading ? "Reinitialisation..." : "Mettre a jour le mot de passe"}
                </Button>
                <button type="button" className="support-text-link" onClick={() => setMode("login")}>Retour a la connexion</button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );

  const renderTicketsView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <div className="support-section-head">
          <CardTitle className="heading-2">
            {selectedClientId && user.role === "admin"
              ? "Tickets du client"
              : user.role === "developer"
                ? "Tickets attribues"
                : "Tickets recents"}
          </CardTitle>
          <div className="support-view-switch" role="tablist" aria-label="Mode d'affichage des tickets">
            <button
              type="button"
              className={`support-view-switch-btn ${ticketDisplayMode === "list" ? "is-active" : ""}`}
              onClick={() => setTicketDisplayMode("list")}
            >
              <LayoutList className="w-4 h-4" />
              Liste
            </button>
            <button
              type="button"
              className={`support-view-switch-btn ${ticketDisplayMode === "kanban" ? "is-active" : ""}`}
              onClick={() => setTicketDisplayMode("kanban")}
            >
              <LayoutGrid className="w-4 h-4" />
              Kanban
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="support-ticket-feed">
        {loadingData && <p className="caption">Chargement...</p>}
        {ticketDisplayMode === "list" &&
          filteredTickets.map((ticketItem) => (
            <button
              key={ticketItem.id}
              type="button"
              className={`support-ticket-row ${selectedTicketId === ticketItem.id ? "is-active" : ""}`}
              onClick={() => openTicket(ticketItem.id)}
            >
                <div className="support-ticket-row-head">
                  <strong>{ticketItem.ticket_number}</strong>
                  <span>{ticketItem.status_label}</span>
                </div>
                <p>{ticketItem.title}</p>
                <small>
                  {ticketItem.company_name} · Mis a jour le {formatDate(ticketItem.updated_at)}
                </small>
                {ticketItem.assigned_developer_name && (
                  <small>Developpeur: {ticketItem.assigned_developer_name}</small>
                )}
                <small>{getPreviewText(ticketItem.description, 110)}</small>
              </button>
            ))}
        {ticketDisplayMode === "kanban" && (
          <div className="support-kanban-board">
            {kanbanColumns.map((column) => (
              <section key={column.value} className="support-kanban-column">
                <div className="support-kanban-column-head">
                  <strong>{column.label}</strong>
                  <span>{column.tickets.length}</span>
                </div>
                <div className="support-kanban-stack">
                  {column.tickets.map((ticketItem) => (
                    <button
                      key={ticketItem.id}
                      type="button"
                      className={`support-kanban-card ${selectedTicketId === ticketItem.id ? "is-active" : ""}`}
                      onClick={() => openTicket(ticketItem.id)}
                    >
                      <strong>{ticketItem.ticket_number}</strong>
                      <p>{ticketItem.title}</p>
                      <small>{getPreviewText(ticketItem.description, 90)}</small>
                      <small>{ticketItem.company_name}</small>
                      {ticketItem.assigned_developer_name && (
                        <small>Dev: {ticketItem.assigned_developer_name}</small>
                      )}
                      <small>Maj le {formatDate(ticketItem.updated_at)}</small>
                    </button>
                  ))}
                  {column.tickets.length === 0 && (
                    <div className="support-kanban-empty">Aucun ticket</div>
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
        {!loadingData && filteredTickets.length === 0 && (
          <p className="body-small">Aucun ticket a afficher.</p>
        )}
      </CardContent>
    </Card>
  );

  const renderTicketDetailView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <div className="support-detail-header">
          <button type="button" className="btn-secondary support-back-btn" onClick={() => setActiveView("tickets")}>
            <ArrowLeft className="w-4 h-4" />
            Retour aux tickets
          </button>
          <CardTitle className="heading-2">
            {selectedTicket ? selectedTicket.ticket_number : "Detail du ticket"}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="support-ticket-detail">
        {!selectedTicket && <p className="body-small">Aucun ticket selectionne.</p>}
        {selectedTicket && (
          <>
            <div className="support-ticket-meta">
              <span className="support-pill"><Clock3 className="w-4 h-4" />{selectedTicket.status_label}</span>
              <span className="support-pill"><FileText className="w-4 h-4" />{selectedTicket.category}</span>
              <span className="support-pill"><Mail className="w-4 h-4" />{selectedTicket.client_email}</span>
              {user.role !== "client" && (
                <span className="support-pill">
                  <Code2 className="w-4 h-4" />
                  {selectedTicket.assigned_developer_name || "Non attribue"}
                </span>
              )}
            </div>

            <div className="support-ticket-panel">
              <h3 className="heading-3">{selectedTicket.title}</h3>
              {user.role !== "client" && selectedTicket.development_time_hours !== null && selectedTicket.development_time_hours !== undefined && (
                <p className="body-small">
                  Temps de developpement saisi: {Number(selectedTicket.development_time_hours || 0).toFixed(2)} h
                  {selectedTicket.development_time_updated_by ? ` par ${selectedTicket.development_time_updated_by}` : ""}
                </p>
              )}
              <div
                className="support-rich-content body-medium"
                dangerouslySetInnerHTML={{ __html: selectedTicket.description }}
              />
            </div>

            <div className="support-ticket-panel">
              <h3 className="heading-3">Conversation</h3>
              {selectedTicket.messages.map((message) => (
                <div key={message.id} className="support-message">
                  <div className="support-message-head">
                    <strong>{message.author_name}</strong>
                    <small>{formatDate(message.created_at)}</small>
                  </div>
                  <div
                    className="support-rich-content"
                    dangerouslySetInnerHTML={{ __html: message.content }}
                  />
                </div>
              ))}
              <form onSubmit={handleSendMessage} className="support-form-stack">
                <RichTextEditor
                  value={messageDraft}
                  onChange={setMessageDraft}
                  placeholder="Ajouter une reponse, une liste ou un bloc code"
                  minHeight={160}
                />
                <Button type="submit" className="btn-secondary" disabled={loading}>Envoyer le message</Button>
              </form>
            </div>

            <div className="support-ticket-panel">
              <h3 className="heading-3">Pieces jointes</h3>
              {selectedTicket.attachments.length > 0 ? selectedTicket.attachments.map((attachment) => (
                <div key={attachment.id} className="support-history-item">
                  <div>
                    <strong>{attachment.filename}</strong>
                    <p>{attachment.content_type} - {(attachment.size / 1024).toFixed(1)} Ko</p>
                  </div>
                  <div className="support-attachment-actions">
                    <small>{formatDate(attachment.uploaded_at)}</small>
                    <button type="button" className="btn-secondary" onClick={() => downloadAttachment(attachment)}>
                      Telecharger
                    </button>
                  </div>
                </div>
              )) : <p className="body-small">Aucune piece jointe sur ce ticket.</p>}
            </div>

            <div className="support-ticket-panel">
              <h3 className="heading-3">Historique des statuts</h3>
              {selectedTicket.status_history.map((entry) => (
                <div key={entry.id} className="support-history-item">
                  <div>
                    <strong>{entry.label}</strong>
                    {entry.comment ? (
                      <div
                        className="support-rich-content"
                        dangerouslySetInnerHTML={{ __html: entry.comment }}
                      />
                    ) : (
                      <p>Sans commentaire</p>
                    )}
                  </div>
                  <small>{entry.changed_by} - {formatDate(entry.changed_at)}</small>
                </div>
              ))}
            </div>

            {user.role === "admin" && (
              <div className="support-admin-box">
                <h3 className="heading-3">Affectation developpeur</h3>
                <select
                  className="form-input"
                  value={selectedTicket.assigned_developer_id || ""}
                  onChange={(event) => handleTicketAssignmentChange(event.target.value)}
                  disabled={loading}
                >
                  <option value="">Aucun developpeur</option>
                  {activeDevelopers.map((developer) => (
                    <option key={developer.id} value={developer.id}>{developer.full_name}</option>
                  ))}
                </select>
                <p className="body-small">Le developpeur attribue recoit un email et verra ce ticket dans son espace.</p>
              </div>
            )}

            {(user.role === "admin" || user.role === "developer") && (
              <form onSubmit={handleStatusUpdate} className="support-admin-box">
                <h3 className="heading-3">Pilotage du ticket</h3>
                <select className="form-input" value={adminStatus} onChange={(e) => setAdminStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.25"
                  placeholder="Temps passe en heures"
                  value={developmentTimeHours}
                  onChange={(e) => setDevelopmentTimeHours(e.target.value)}
                />
                <RichTextEditor
                  value={adminComment}
                  onChange={setAdminComment}
                  placeholder="Commentaire visible par le client, avec mise en forme si besoin"
                  minHeight={150}
                />
                <Button type="submit" className="btn-primary" disabled={loading}>Mettre a jour le statut</Button>
              </form>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  const renderNotificationsView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">Notifications</CardTitle>
      </CardHeader>
      <CardContent className="support-sidebar-content">
        {notifications.map((notification) => (
          <button
            type="button"
            key={notification.id}
            className={`support-notification-item ${notification.read ? "" : "is-unread"}`}
            onClick={() => markAsRead(notification.id)}
          >
            <div className="support-notification-head">
              <strong>{notification.title}</strong>
              {!notification.read && <CircleAlert className="w-4 h-4" />}
            </div>
            <p>{notification.message}</p>
            <small>{formatDate(notification.created_at)}</small>
          </button>
        ))}
        {notifications.length === 0 && <p className="body-small">Aucune notification pour le moment.</p>}
      </CardContent>
    </Card>
  );

  const renderProfileView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">
          {user.role === "admin" ? "Infos support" : user.role === "developer" ? "Infos developpeur" : "Infos client"}
        </CardTitle>
      </CardHeader>
      <CardContent className="support-profile-grid">
        <div className="support-info-card">
          <span className="caption">Nom</span>
          <strong>{user.full_name}</strong>
        </div>
        <div className="support-info-card">
          <span className="caption">Entreprise</span>
          <strong>{user.company_name}</strong>
        </div>
        <div className="support-info-card">
          <span className="caption">Email</span>
          <strong>{user.email}</strong>
        </div>
        <div className="support-info-card">
          <span className="caption">Tickets</span>
          <strong>{tickets.length}</strong>
        </div>
        {user.role === "client" && user.tma_enabled && user.tma_visible_to_client && (
          <>
            <div className="support-info-card">
              <span className="caption">Forfait TMA</span>
              <strong>{Number(user.tma_hours_total || 0).toFixed(2)} h</strong>
            </div>
            <div className="support-info-card">
              <span className="caption">TMA restante</span>
              <strong>{Number(user.tma_hours_remaining || 0).toFixed(2)} h</strong>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  const renderNewTicketView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">Nouvelle demande de modification</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleCreateTicket} className="support-form-grid">
          <input className="form-input" type="text" placeholder="Titre de la demande" value={ticketForm.title} onChange={(e) => setTicketForm({ ...ticketForm, title: e.target.value })} required />
          <select className="form-input" value={ticketForm.category} onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}>
            <option value="modification-contenu">Modification de contenu</option>
            <option value="ajout-page">Ajout de page</option>
            <option value="correction-technique">Correction technique</option>
            <option value="seo">SEO</option>
          </select>
          <input className="form-input support-span-2" type="url" placeholder="URL du site concerne" value={ticketForm.site_url} onChange={(e) => setTicketForm({ ...ticketForm, site_url: e.target.value })} required />
          <div className="support-span-2">
            <RichTextEditor
              value={ticketForm.description}
              onChange={(value) => setTicketForm({ ...ticketForm, description: value })}
              placeholder="Expliquez precisement la modification souhaitee. Vous pouvez utiliser des listes et des blocs code."
              minHeight={220}
            />
          </div>
          <div className="support-span-2">
            <label className="form-label">Pieces jointes ({MAX_TICKET_ATTACHMENTS} max, PDF/JPG/PNG/DOCX)</label>
            <input className="form-input" type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.docx" onChange={handleTicketFilesChange} />
            <p className="body-small">Vous pouvez ajouter jusqu'a {MAX_TICKET_ATTACHMENTS} fichiers, 5 Mo maximum par fichier.</p>
          </div>
          <Button type="submit" className="btn-primary support-span-2" disabled={loading}>
            {loading ? "Envoi..." : "Envoyer le ticket"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );

  const renderClientEditorForm = () => (
    <div className="support-inline-editor">
      <div className="support-inline-editor-head">
        <div className="support-panel-heading">
          <h3 className="heading-3">{isEditingClient ? "Modifier un client" : "Ajouter un acces client"}</h3>
          <p className="body-small">
            {isEditingClient
              ? "Modifie le nom, l'email, le referent et le forfait TMA."
              : "Cree un acces client avec un mot de passe initial."}
          </p>
        </div>
        <button type="button" className="support-inline-close" onClick={resetClientForm} aria-label="Fermer le bloc d'edition">
          ×
        </button>
      </div>
      <form onSubmit={handleCreateClient} className="support-form-grid">
        <input
          className="form-input"
          type="text"
          placeholder="Nom du client"
          value={adminClientForm.full_name}
          onChange={(e) => setAdminClientForm({ ...adminClientForm, full_name: e.target.value })}
          required
        />
        <input
          className="form-input"
          type="email"
          placeholder="Email du client"
          value={adminClientForm.email}
          onChange={(e) => setAdminClientForm({ ...adminClientForm, email: e.target.value })}
          required
        />
        <div className="support-span-2">
          <PasswordField
            value={adminClientForm.password}
            onChange={(e) => setAdminClientForm({ ...adminClientForm, password: e.target.value })}
            placeholder={isEditingClient ? "Nouveau mot de passe (optionnel)" : "Mot de passe initial"}
            required={!isEditingClient}
            isVisible={showAdminClientPassword}
            onToggleVisibility={() => setShowAdminClientPassword((current) => !current)}
          />
        </div>
        <div className="support-span-2">
          <PasswordRequirements password={adminClientForm.password} />
        </div>
        <select
          className="form-input support-span-2"
          value={adminClientForm.managed_developer_id}
          onChange={(e) => setAdminClientForm({ ...adminClientForm, managed_developer_id: e.target.value })}
        >
          <option value="">Aucun developpeur referent</option>
          {activeDevelopers.map((developer) => (
            <option key={developer.id} value={developer.id}>{developer.full_name}</option>
          ))}
        </select>
        <label className="support-checkbox-line support-span-2">
          <input
            type="checkbox"
            checked={adminClientForm.tma_enabled}
            onChange={(e) => setAdminClientForm({
              ...adminClientForm,
              tma_enabled: e.target.checked,
              tma_visible_to_client: e.target.checked ? adminClientForm.tma_visible_to_client : false,
            })}
          />
          Activer un forfait TMA pour ce client
        </label>
        {adminClientForm.tma_enabled && (
          <>
            <input
              className="form-input"
              type="number"
              min="0"
              step="0.25"
              placeholder="Heures de TMA vendues"
              value={adminClientForm.tma_hours_total}
              onChange={(e) => setAdminClientForm({ ...adminClientForm, tma_hours_total: e.target.value })}
            />
            <label className="support-checkbox-line">
              <input
                type="checkbox"
                checked={adminClientForm.tma_visible_to_client}
                onChange={(e) => setAdminClientForm({ ...adminClientForm, tma_visible_to_client: e.target.checked })}
              />
              Afficher au client
            </label>
          </>
        )}
        <p className="body-small support-auth-note support-span-2">
          {isEditingClient
            ? "Laisse le mot de passe vide pour ne pas le modifier."
            : "Le client pourra changer son mot de passe plus tard via Mot de passe oublie."}
          {" "}Le developpeur referent recevra les prochains tickets de ce client.
        </p>
        <div className="support-form-actions support-span-2">
          <Button type="submit" className="btn-primary" disabled={loading}>
            {loading ? (isEditingClient ? "Mise a jour..." : "Creation...") : (isEditingClient ? "Mettre a jour" : "Creer l'acces")}
          </Button>
          <Button type="button" className="btn-secondary" onClick={resetClientForm} disabled={loading}>
            Annuler
          </Button>
          {isEditingClient && (
            <Button
              type="button"
              className="btn-secondary support-danger-btn"
              onClick={() => setIsDeleteClientDialogOpen(true)}
              disabled={loading}
            >
              Bloquer le client
            </Button>
          )}
        </div>
      </form>
    </div>
  );

  const renderClientsView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">Clients</CardTitle>
      </CardHeader>
      <CardContent className="support-management-list">
        <div className="support-list-toolbar">
          <div>
            <p className="body-small">Clique une ligne pour modifier, ou ajoute un nouvel acces.</p>
          </div>
          <Button type="button" className="btn-primary" onClick={openCreateClientForm}>
            Ajouter un client
          </Button>
        </div>
        {isClientEditorOpen && !isEditingClient && renderClientEditorForm()}

        <div className="support-table-list">
          <div className="support-table-row support-table-head" aria-hidden="true">
            <span>Client</span>
            <span>Email</span>
            <span>Referent</span>
            <span>Tickets ouverts</span>
            <span>Forfait TMA</span>
          </div>
          {adminClients.map((client) => (
            <div key={client.id} className="support-table-item">
              <button
                type="button"
                className={`support-table-row ${adminClientForm.id === client.id ? "is-active" : ""}`}
                onClick={() => handleEditClient(client)}
              >
                <strong>{client.full_name}</strong>
                <span>{client.email}</span>
                <span>{client.managed_developer_name || "Aucun referent"}</span>
                <span>{client.open_ticket_count} ouverts</span>
                <span>{client.tma_enabled ? `${Number(client.tma_hours_remaining || 0).toFixed(2)} h TMA restantes` : "TMA inactive"}</span>
              </button>
              {isClientEditorOpen && adminClientForm.id === client.id && renderClientEditorForm()}
            </div>
          ))}
          {adminClients.length === 0 && <p className="body-small">Aucun client pour le moment.</p>}
        </div>
      </CardContent>
    </Card>
  );

  const renderDeveloperEditorForm = () => (
    <div className="support-inline-editor">
      <div className="support-inline-editor-head">
        <div className="support-panel-heading">
          <h3 className="heading-3">{isEditingDeveloper ? "Modifier un developpeur" : "Ajouter un developpeur"}</h3>
          <p className="body-small">
            Gere l'acces plateforme et conserve l'historique meme si le compte est bloque.
          </p>
        </div>
        <button type="button" className="support-inline-close" onClick={resetDeveloperForm} aria-label="Fermer le bloc d'edition">
          ×
        </button>
      </div>
      <form onSubmit={handleCreateDeveloper} className="support-form-grid">
        <input
          className="form-input"
          type="text"
          placeholder="Nom du developpeur"
          value={adminDeveloperForm.full_name}
          onChange={(e) => setAdminDeveloperForm({ ...adminDeveloperForm, full_name: e.target.value })}
          required
        />
        <input
          className="form-input"
          type="email"
          placeholder="Email du developpeur"
          value={adminDeveloperForm.email}
          onChange={(e) => setAdminDeveloperForm({ ...adminDeveloperForm, email: e.target.value })}
          required
        />
        <div className="support-span-2">
          <PasswordField
            value={adminDeveloperForm.password}
            onChange={(e) => setAdminDeveloperForm({ ...adminDeveloperForm, password: e.target.value })}
            placeholder={isEditingDeveloper ? "Nouveau mot de passe (optionnel)" : "Mot de passe initial"}
            required={!isEditingDeveloper}
            isVisible={showAdminDeveloperPassword}
            onToggleVisibility={() => setShowAdminDeveloperPassword((current) => !current)}
          />
        </div>
        <div className="support-span-2">
          <PasswordRequirements password={adminDeveloperForm.password} />
        </div>
        <label className="support-checkbox-line support-span-2">
          <input
            type="checkbox"
            checked={adminDeveloperForm.can_access_platform}
            onChange={(e) => setAdminDeveloperForm({ ...adminDeveloperForm, can_access_platform: e.target.checked })}
          />
          Acces autorise a la plateforme
        </label>
        <div className="support-form-actions support-span-2">
          <Button type="submit" className="btn-primary" disabled={loading}>
            {loading ? (isEditingDeveloper ? "Mise a jour..." : "Creation...") : (isEditingDeveloper ? "Mettre a jour" : "Creer l'acces")}
          </Button>
          <Button type="button" className="btn-secondary" onClick={resetDeveloperForm} disabled={loading}>
            Annuler
          </Button>
          {isEditingDeveloper && (
            <Button
              type="button"
              className="btn-secondary support-danger-btn"
              onClick={handleDeleteDeveloper}
              disabled={loading}
            >
              Bloquer le compte
            </Button>
          )}
        </div>
      </form>
    </div>
  );

  const renderDevelopersView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">Developpeurs</CardTitle>
      </CardHeader>
      <CardContent className="support-management-list">
        <div className="support-list-toolbar">
          <div>
            <p className="body-small">Clique une ligne pour modifier, ou ajoute un nouvel acces.</p>
          </div>
          <Button type="button" className="btn-primary" onClick={openCreateDeveloperForm}>
            Ajouter un developpeur
          </Button>
        </div>
        {isDeveloperEditorOpen && !isEditingDeveloper && renderDeveloperEditorForm()}

        <div className="support-table-list">
          <div className="support-table-row support-table-head" aria-hidden="true">
            <span>Developpeur</span>
            <span>Email</span>
            <span>Acces</span>
            <span>Tickets attribues</span>
            <span>Clients geres</span>
          </div>
          {adminDevelopers.map((developer) => (
            <div key={developer.id} className="support-table-item">
              <button
                type="button"
                className={`support-table-row ${adminDeveloperForm.id === developer.id ? "is-active" : ""}`}
                onClick={() => handleEditDeveloper(developer)}
              >
                <strong>{developer.full_name}</strong>
                <span>{developer.email}</span>
                <span>{developer.can_access_platform && developer.is_active ? "Actif" : "Bloque"}</span>
                <span>{developer.assigned_ticket_count} ticket(s) attribue(s)</span>
                <span>{developer.managed_client_count} client(s) gere(s)</span>
              </button>
              {isDeveloperEditorOpen && adminDeveloperForm.id === developer.id && renderDeveloperEditorForm()}
            </div>
          ))}
          {adminDevelopers.length === 0 && <p className="body-small">Aucun developpeur pour le moment.</p>}
        </div>
      </CardContent>
    </Card>
  );

  const renderConnectedView = () => (
    <section className="support-shell">
      <div className="container support-app-layout">
        <aside className="support-app-sidebar">
          <div className="support-app-sidebar-top">
            <div className="support-app-brand">
              <span className="support-app-brand-mark">{user.role === "admin" ? "A" : user.role === "developer" ? "D" : "C"}</span>
              <div>
                <strong>{user.company_name}</strong>
                <p>{user.full_name}</p>
              </div>
            </div>

            <nav className="support-nav">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`support-nav-item ${activeView === item.id ? "is-active" : ""}`}
                    onClick={() => setActiveView(item.id)}
                  >
                    <span className="support-nav-item-main">
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </span>
                    {item.badge > 0 && <span className="support-nav-badge">{item.badge}</span>}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="support-app-sidebar-bottom">
            <button type="button" className="support-nav-item" onClick={handleLogout}>
              <span className="support-nav-item-main">
                <LogOut className="w-4 h-4" />
                Deconnexion
              </span>
            </button>
          </div>
        </aside>

        <main className="support-app-main">
          <div className="support-app-main-header">
            <div>
              <h1 className="heading-2">
                {activeView === "tickets" && "Tickets"}
                {activeView === "ticket-detail" && "Detail du ticket"}
                {activeView === "new-ticket" && "Ajouter un ticket"}
                {activeView === "notifications" && "Notifications"}
                {activeView === "profile" && (user.role === "admin" ? "Infos support" : user.role === "developer" ? "Infos developpeur" : "Infos client")}
                {activeView === "clients" && "Clients"}
                {activeView === "developers" && "Developpeurs"}
              </h1>
              <p className="body-small">
                {activeView === "tickets" && "Du plus recent au moins recent"}
                {activeView === "ticket-detail" && "Vue detaillee d'un ticket avec conversation, pieces jointes et suivi"}
                {activeView === "clients" && "Vue admin pour gerer les clients et filtrer leurs tickets"}
                {activeView === "developers" && "Gestion des comptes developpeurs et des acces plateforme"}
                {activeView === "notifications" && "Suivi des alertes de l'espace support"}
                {activeView === "new-ticket" && "Creation d'une nouvelle demande"}
                {activeView === "profile" && "Informations du compte courant"}
              </p>
            </div>
            {selectedClientId && (activeView === "tickets" || activeView === "ticket-detail") && user.role === "admin" && (
              <button type="button" className="btn-secondary" onClick={() => setSelectedClientId("")}>
                Tous les clients
              </button>
            )}
          </div>

          {activeView === "tickets" && renderTicketsView()}
          {activeView === "ticket-detail" && renderTicketDetailView()}
          {activeView === "new-ticket" && user.role === "client" && renderNewTicketView()}
          {activeView === "notifications" && renderNotificationsView()}
          {activeView === "profile" && renderProfileView()}
          {activeView === "clients" && user.role === "admin" && renderClientsView()}
          {activeView === "developers" && user.role === "admin" && renderDevelopersView()}
        </main>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-page)" }}>
      <Header />
      {!user ? renderAuthView() : renderConnectedView()}
      <AlertDialog open={isDeleteClientDialogOpen} onOpenChange={setIsDeleteClientDialogOpen}>
        <AlertDialogContent className="support-alert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              {adminClientForm.full_name
                ? `Cette action bloquera l'acces de ${adminClientForm.full_name}. Ses tickets, notifications et pieces jointes resteront conserves.`
                : "Cette action bloquera l'acces de ce client. Ses tickets, notifications et pieces jointes resteront conserves."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="support-alert-danger"
              onClick={(event) => {
                event.preventDefault();
                handleDeleteClient();
              }}
              disabled={loading}
            >
              {loading ? "Blocage..." : "Bloquer l'acces"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
